// Import necessary dependencies and components
import React, { useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import LayoutWithNavbar from "./layout/LayoutWithNavbar";
import AdminLayout from "./layout/AdminLayout";
import Home from "./pages/Home";
import Projects from "./pages/Projects";
import Navbar from "./components/common/Navbar";
import Footer from "./components/common/Footer";
import ProjectView from "./pages/ProjectView";
import CreateProject from "./pages/CreateProject";
import { ToastContainer } from "react-toastify";
import { useAccount, useDisconnect } from "wagmi";
import PageNotFound from "./pages/PageNotFound";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import VerifyEmail from "./pages/VerifyEmail";
import AcceptInvitation from "./pages/AcceptInvitation";
import LayoutWithoutFooter from "./layout/LayoutWithoutFooter";
import PopupLoading from "./components/common/PopupLoading";
import useTokenExpiry from "./hooks/useTokenExpiry";
import Notifications from "./pages/Notifications";
import FAQ from "./pages/FAQ";
import Users from "./pages/Users";
import {
  selectGeneral,
  setIsLoggedIn,
  setScrolledToTop,
} from "./redux/slices/generalSlice";
import { useDispatch, useSelector } from "react-redux";
import Terms from "./pages/Terms";
import RegisterModal from "./components/auth/RegisterModal";
import AuthModal from "./components/auth/AuthModal";
import WalletRequiredModal from "./components/auth/WalletRequiredModal";
import useSocketConnection from "./hooks/useSocketConnection";
import CreateProfile from "./pages/CreateProfile";
import ViewProfile from "./pages/ViewProfile";
import CreateResult from "./pages/CreateResult";
import ResultDetail from "./pages/ResultDetail";
import Discovery from "./pages/Discovery";
import Communities from "./pages/Communities";
import CommunityView from "./pages/CommunityView";
import CreateCommunity from "./pages/CreateCommunity";
import AccountPopup from "./components/common/AccountPopup";
import NetworkPopup from "./components/common/NetworkPopup";
import ExpireSessionPopup from "./components/common/ExpireSessionPopup";
import { privateRoutes } from "./data/general";
import MetaTags from "./components/common/MetaTags";
import AuthCallback from "./pages/AuthCallback";
import OAuthLinkCallback from "./pages/OAuthLinkCallback";
import OAuthLinkResult from "./pages/OAuthLinkResult";
import ResetPassword from "./pages/ResetPassword";
import TelegramVerify from "./pages/TelegramVerify";
import TelegramCallback from "./pages/TelegramCallback";
import LoginRedirect from "./pages/LoginRedirect";
import About from "./pages/About";
import Contact from "./pages/Contact";
import {
  AdminLogin,
  AdminDashboard,
  AdminUsers,
  AdminUserDetail,
  AdminProjects,
  AdminCommunities,
  AdminResults,
  AdminFunds,
} from "./pages/admin";

// Main App component
const App = () => {
  // Destructure values from the Redux store using useSelector
  const {
    showRegisterModal,
    loginLoading,
    showAccountPopup,
    showNetworkPopup,
    showExpireSessionPopup,
    showAuthModal,
    showWalletRequiredModal,
  } = useSelector(selectGeneral);

  // Destructure values from React Router's useLocation hook
  const { pathname } = useLocation();
  
  const dispatch = useDispatch();

  // Function to handle wallet disconnection
  // In the new system, wallet disconnection does NOT log out the user
  // since wallet is just a feature, not an authentication method
  const handleDisconnect = async () => {
    // Wallet disconnection no longer affects authentication
    // User remains logged in via email/password or Google OAuth
    // Just clear the wallet-related data
    localStorage.removeItem("public_address");
  };

  // Custom hook for handling wallet connections and disconnections
  const { status, isConnected, address } = useAccount();
  const [wasConnected, setWasConnected] = useState(false);

  // When wallet connects, just store the address
  // Wallet is a FEATURE, not authentication
  useEffect(() => {
    if (isConnected && address) {
      setWasConnected(true);
      // Store the wallet address for reference (used for blockchain actions)
      localStorage.setItem("public_address", address.toLowerCase());
    }
  }, [isConnected, address]);

  // Check if user is logged in on app load
  useEffect(() => {
    const hasToken = localStorage.getItem("token");
    const userRole = localStorage.getItem("role");
    if (hasToken && userRole && userRole !== "un-registered") {
      dispatch(setIsLoggedIn(true));
    }
  }, [dispatch]);

  // Handle wallet disconnection - just clear wallet data, don't log out
  useEffect(() => {
    if (status === "disconnected" && wasConnected) {
      handleDisconnect();
      setWasConnected(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, wasConnected]);

  // Custom hook for handling disconnections
  useDisconnect({
    onSuccess() {
      // Wallet disconnection no longer affects authentication
      // Just clear wallet address
      localStorage.removeItem("public_address");
    },
  });

  useEffect(() => {
    dispatch(setScrolledToTop(false));
  }, [pathname, dispatch]);

  // Custom hook for handling socket connections
  useSocketConnection();

  // Check token expiry on app load and periodically
  useTokenExpiry({
    showNotification: true,
  });

  // Main App component render
  return (
    <div className="w-full h-full flex flex-col items-center bg-whitesmoke">
      <MetaTags addPostfixTitle={false} />
      {/* ToastContainer for displaying notifications */}
      <ToastContainer
        position="top-right"
        autoClose={10000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{
          whiteSpace: "pre-line",
        }}
        theme="light"
      />
      {/* React Router Routes */}
      <Routes>
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:id" element={<AdminUserDetail />} />
          <Route path="projects" element={<AdminProjects />} />
          <Route path="communities" element={<AdminCommunities />} />
          <Route path="results" element={<AdminResults />} />
          <Route path="funds" element={<AdminFunds />} />
        </Route>

        <Route
          element={
            <>
              <Navbar isDark />
              <Outlet />
              <Footer />
            </>
          }
        >
          <Route path="/" element={<Home />} />
        </Route>
        <Route element={<LayoutWithNavbar />}>
          {/* Catch-all route for PageNotFound */}
          <Route path="*" element={<PageNotFound />} />
          {/* Navigate to home if no matching route is found */}
          <Route path="/" element={<Navigate to={"/"} />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:id/*" element={<ProjectView />} />
          <Route path="/create-profile" element={<CreateProfile />} />
          <Route path="/profile" element={<ViewProfile />} />
          <Route path="/create-project" element={<CreateProject />} />
          <Route path="/create-result" element={<CreateResult />} />
          <Route path="/results/:id/*" element={<ResultDetail />} />
          <Route path="/discovery" element={<Discovery />} />
          <Route path="/communities" element={<Communities />} />
          <Route path="/communities/:id" element={<CommunityView />} />
          <Route path="/create-community" element={<CreateCommunity />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/FAQ" element={<FAQ />} />
          <Route path="/terms-of-service" element={<Terms />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/users" element={<Users />} />
        </Route>
        <Route element={<LayoutWithoutFooter />}>
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/link/:provider/callback" element={<OAuthLinkCallback />} />
          <Route path="/auth/link-result" element={<OAuthLinkResult />} />
          <Route path="/telegram/verify/:token" element={<TelegramVerify />} />
          <Route path="/telegram/callback" element={<TelegramCallback />} />
          <Route path="/login" element={<LoginRedirect />} />
        </Route>
      </Routes>
      {/* Loading popup during login */}
      {loginLoading && <PopupLoading loadingText="logging in " />}
      {/* Register modal component */}
      {showRegisterModal && <RegisterModal />}
      {/* New auth modal for social login */}
      {showAuthModal && <AuthModal />}
      {/* Wallet required modal for blockchain actions */}
      {showWalletRequiredModal && <WalletRequiredModal />}
      {/* Account popup component */}
      {showAccountPopup && <AccountPopup />}
      {/* Network popup component */}
      {showNetworkPopup && <NetworkPopup />}
      {/* Expire session popup component */}
      {showExpireSessionPopup && <ExpireSessionPopup />}
    </div>
  );
};

// Export the App component as the default export
export default App;
